// frontend/src/screens/user/MyMonthlyView.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLayout } from '../context/LayoutContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  Chip,
  Tooltip,
  Breadcrumbs,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  Print,
  CalendarMonth,
  NavigateNext,
  Info,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useGetMonthlyShiftsByStaffQuery,
} from '../slices/shiftApiSlice';
import {
  useGetMonthlyAvailabilitiesByStaffQuery,
} from '../slices/availabilityApiSlice';
import { useSelector } from 'react-redux';

// Color coding for different shift/availability statuses
const STATUS_COLORS = {
  assigned: '#4caf50',
  weekendAssigned: '#ff9800',
  agency: '#9c27b0',
  cancelled: '#f44336',
  sickLeave: '#ff5252',
  sickLeavePartial: '#ff9800',
  unavailability: '#d32f2f',
  availabilityPending: '#e3f2fd',
  availabilityApproved: '#e8f5e8',
  availabilityRejected: '#ffebee',
};

// Month names for selector
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Helper function to format hours and minutes
const formatHoursMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes === 0) return '0h 0m';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// Helper function to format time
const formatTime = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Helper function to compare objects deeply
const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

// Print styles component
const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print {
        display: none !important;
      }
      .print-page {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      body {
        margin: 0 !important;
        padding: 20px !important;
        background: white !important;
      }
      .print-header {
        text-align: center !important;
        margin-bottom: 20px !important;
        border-bottom: 2px solid #000 !important;
        padding-bottom: 10px !important;
      }
      .month-calendar {
        width: 100% !important;
        border-collapse: collapse !important;
      }
      .month-calendar th {
        background-color: #1976d2 !important;
        color: white !important;
        padding: 10px !important;
        text-align: center !important;
        border: 1px solid #ddd !important;
      }
      .month-calendar td {
        border: 1px solid #ddd !important;
        vertical-align: top !important;
        height: 100px !important;
        padding: 5px !important;
      }
      .day-number {
        font-weight: bold !important;
        margin-bottom: 5px !important;
      }
      .shift-card {
        padding: 3px !important;
        margin: 2px 0 !important;
        border-radius: 3px !important;
        font-size: 8px !important;
        color: white !important;
        page-break-inside: avoid !important;
      }
      .shift-assigned { background-color: #4caf50 !important; }
      .shift-weekend { background-color: #ff9800 !important; }
      .shift-agency { background-color: #9c27b0 !important; }
      .shift-cancelled { background-color: #f44336 !important; }
      .availability-card {
        padding: 2px !important;
        margin: 1px 0 !important;
        font-size: 7px !important;
        border-left: 3px solid !important;
        page-break-inside: avoid !important;
      }
      .unavailable-card {
        background-color: #d32f2f !important;
        color: white !important;
        padding: 2px !important;
        margin: 1px 0 !important;
        font-size: 7px !important;
        page-break-inside: avoid !important;
      }
      @page {
        size: landscape;
        margin: 0.5in;
      }
    }
  `}</style>
);

const MyMonthlyView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  // State
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(new Date(selectedYear, selectedMonth, 1));
  const [monthDays, setMonthDays] = useState([]);
  const [myShifts, setMyShifts] = useState({});
  const [myAvailabilities, setMyAvailabilities] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Update selectedDate when year or month changes
  useEffect(() => {
    setSelectedDate(new Date(selectedYear, selectedMonth, 1));
  }, [selectedYear, selectedMonth]);

  // Calculate month days
  const getMonthDays = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
    let firstDayOfWeek = firstDay.getDay();
    // Adjust to make Monday the first day (1 = Monday, 0 = Sunday becomes 7)
    firstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;
    
    // Calculate days from previous month to show
    const daysFromPrevMonth = firstDayOfWeek - 1;
    
    const days = [];
    
    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const dayDate = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date: dayDate.toISOString().split('T')[0],
        dayOfMonth: prevMonthLastDay - i,
        isCurrentMonth: false,
        isToday: false,
        dayName: dayDate.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: dayDate,
      });
    }
    
    // Add current month days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dayDate = new Date(year, month, i);
      const dateStr = dayDate.toISOString().split('T')[0];
      const isToday = dayDate.getTime() === today.getTime();
      
      days.push({
        date: dateStr,
        dayOfMonth: i,
        isCurrentMonth: true,
        isToday: isToday,
        dayName: dayDate.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: dayDate,
        isWeekend: dayDate.getDay() === 0 || dayDate.getDay() === 6,
      });
    }
    
    // Calculate days needed from next month to complete the grid (42 days total - 6 weeks)
    const totalDaysNeeded = 42;
    const daysFromNextMonth = totalDaysNeeded - days.length;
    
    for (let i = 1; i <= daysFromNextMonth; i++) {
      const dayDate = new Date(year, month + 1, i);
      days.push({
        date: dayDate.toISOString().split('T')[0],
        dayOfMonth: i,
        isCurrentMonth: false,
        isToday: false,
        dayName: dayDate.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: dayDate,
      });
    }
    
    return {
      year,
      month,
      monthName: date.toLocaleDateString('en-US', { month: 'long' }),
      yearMonth: `${year}-${String(month + 1).padStart(2, '0')}`,
      days,
      startDate: days[0].date,
      endDate: days[days.length - 1].date,
    };
  }, []);

  const monthInfo = useMemo(() => getMonthDays(selectedDate), [selectedDate, getMonthDays]);

  // Fetch data
  const { data: shiftsData, isLoading: loadingShifts, refetch: refetchShifts } = useGetMonthlyShiftsByStaffQuery({
    startDate: monthInfo.startDate,
    endDate: monthInfo.endDate,
  });

  const { data: availabilitiesData, isLoading: loadingAvailabilities, refetch: refetchAvailabilities } = useGetMonthlyAvailabilitiesByStaffQuery({
    startDate: monthInfo.startDate,
    endDate: monthInfo.endDate,
  });

  // Layout effect
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);

  // Process my shifts
  useEffect(() => {
    if (shiftsData && userInfo) {
      const shiftsByDate = {};
      monthInfo.days.forEach(day => {
        shiftsByDate[day.date] = [];
      });
      
      // Filter shifts assigned to current user
      const myAssignedShifts = shiftsData.filter(shift => {
        const filledById = shift.filledBy?._id || shift.filledBy;
        return filledById === userInfo._id;
      });
      
      myAssignedShifts.forEach(shift => {
        const shiftDate = new Date(shift.startDateTime).toISOString().split('T')[0];
        const dayOfWeek = new Date(shift.startDateTime).getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const isAgency = !!shift.filledByAgency;
        const isCancelled = shift.lineStatus === 'Cancelled';
        
        if (shiftsByDate[shiftDate]) {
          shiftsByDate[shiftDate].push({
            id: shift._id,
            startTime: formatTime(shift.startDateTime),
            endTime: formatTime(shift.endDateTime),
            ward: shift.wardDepartmentArea,
            location: shift.hospitalLocationId?.name,
            categoryOfStaff: shift.categoryOfStaff,
            isWeekend: isWeekend,
            isAgencyFilled: isAgency,
            isCancelled: isCancelled,
            lineStatus: shift.lineStatus,
          });
        }
      });
      
      if (!isEqual(myShifts, shiftsByDate)) {
        setMyShifts(shiftsByDate);
      }
    }
  }, [shiftsData, userInfo, monthInfo.days]);

  // Process my availabilities
  useEffect(() => {
    if (availabilitiesData && userInfo) {
      const availabilitiesByDate = {};
      monthInfo.days.forEach(day => {
        availabilitiesByDate[day.date] = [];
      });
      
      // Filter availabilities for current user
      const myAvailabilitiesData = availabilitiesData.filter(avail => {
        const requestedById = avail.requestedBy?._id || avail.requestedBy;
        return requestedById === userInfo._id;
      });
      
      myAvailabilitiesData.forEach(availability => {
        const startDate = new Date(availability.startDateTime);
        const endDate = new Date(availability.endDateTime);
        const dateString = startDate.toISOString().split('T')[0];
        
        if (availabilitiesByDate[dateString]) {
          const dayOfWeek = startDate.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          // Check if this is an unavailability record
          const isUnavailable = availability.isUnavailable || availability.isSickLeave;
          
          availabilitiesByDate[dateString].push({
            id: availability._id,
            startTime: formatTime(availability.startDateTime),
            endTime: formatTime(availability.endDateTime),
            durationMinutes: availability.durationMinutes,
            comments: availability.comments,
            status: availability.status || 'Pending',
            isWeekend: isWeekend,
            categoryOfStaff: availability.categoryOfStaff || 'General',
            ward: availability.wardDepartmentArea || '',
            
            // Unavailability fields
            isSickLeave: availability.isSickLeave || false,
            sickLeaveType: availability.sickLeaveType || 'Full Day',
            sicknessReason: availability.sicknessReason || '',
            
            isUnavailable: availability.isUnavailable || false,
            unavailabilityReason: availability.unavailabilityReason || '',
            unavailabilityType: availability.unavailabilityType || 'Full Day',
          });
        }
      });
      
      if (!isEqual(myAvailabilities, availabilitiesByDate)) {
        setMyAvailabilities(availabilitiesByDate);
      }
    }
  }, [availabilitiesData, userInfo, monthInfo.days]);

  // Navigation functions
  const navigateYear = (direction) => {
    setSelectedYear(prev => prev + direction);
  };

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
      setSelectedYear(newYear);
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
      setSelectedYear(newYear);
    }
    
    setSelectedMonth(newMonth);
  };

  const goToCurrentMonth = () => {
    const today = new Date();
    setSelectedYear(today.getFullYear());
    setSelectedMonth(today.getMonth());
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value));
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(parseInt(event.target.value));
  };

  // Generate year options (current year - 5 to current year + 2)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 1; year <= currentYear + 5; year++) {
      years.push(year);
    }
    return years;
  }, []);

  const formatMonthYear = () => {
    return `${MONTHS[selectedMonth]} ${selectedYear}`;
  };

  // Get shift background color
  const getShiftColor = (shift) => {
    if (shift.isCancelled) return STATUS_COLORS.cancelled;
    if (shift.isAgencyFilled) return STATUS_COLORS.agency;
    if (shift.isWeekend) return STATUS_COLORS.weekendAssigned;
    return STATUS_COLORS.assigned;
  };

  // Get availability background color
  const getAvailabilityColor = (availability) => {
    if (availability.isUnavailable || availability.isSickLeave) {
      return availability.isSickLeave ? STATUS_COLORS.sickLeave : STATUS_COLORS.unavailability;
    }
    switch(availability.status) {
      case 'Approved': return STATUS_COLORS.availabilityApproved;
      case 'Rejected': return STATUS_COLORS.availabilityRejected;
      default: return STATUS_COLORS.availabilityPending;
    }
  };

  // Get availability border color
  const getAvailabilityBorderColor = (availability) => {
    if (availability.isUnavailable || availability.isSickLeave) {
      return availability.isSickLeave ? '#d32f2f' : '#b71c1c';
    }
    switch(availability.status) {
      case 'Approved': return '#4caf50';
      case 'Rejected': return '#f44336';
      default: return '#2196f3';
    }
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      showSnackbar('Please allow pop-ups to print', 'warning');
      return;
    }
    
    // Build printable HTML
    const printHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>My Monthly Schedule - ${formatMonthYear()}</title>
        <style>
          @page {
            size: landscape;
            margin: 0.5in;
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          
          .print-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .print-subtitle {
            font-size: 16px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .print-info {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          
          .month-calendar {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            margin-top: 20px;
          }
          
          .month-calendar th {
            background-color: #1976d2;
            color: white;
            padding: 10px;
            text-align: center;
            border: 1px solid #ddd;
            font-weight: bold;
          }
          
          .month-calendar td {
            border: 1px solid #ddd;
            vertical-align: top;
            height: 120px;
            padding: 5px;
            page-break-inside: avoid;
          }
          
          .other-month {
            background-color: #f5f5f5;
            color: #999;
          }
          
          .current-month {
            background-color: white;
          }
          
          .today-cell {
            background-color: #e3f2fd !important;
            border: 2px solid #1976d2 !important;
          }
          
          .day-number {
            font-weight: bold;
            margin-bottom: 5px;
            font-size: 12px;
          }
          
          .today-number {
            color: #1976d2;
            font-size: 14px;
          }
          
          .shift-card {
            padding: 3px;
            margin: 2px 0;
            border-radius: 3px;
            font-size: 8px;
            color: white;
            font-weight: bold;
            page-break-inside: avoid;
          }
          
          .shift-assigned { background-color: #4caf50 !important; }
          .shift-weekend { background-color: #ff9800 !important; }
          .shift-agency { background-color: #9c27b0 !important; }
          .shift-cancelled { background-color: #f44336 !important; }
          
          .availability-card {
            padding: 2px;
            margin: 1px 0;
            font-size: 7px;
            border-left: 3px solid;
            page-break-inside: avoid;
          }
          
          .availability-pending {
            background-color: #e3f2fd;
            border-left-color: #2196f3;
          }
          
          .availability-approved {
            background-color: #e8f5e8;
            border-left-color: #4caf50;
          }
          
          .availability-rejected {
            background-color: #ffebee;
            border-left-color: #f44336;
          }
          
          .unavailable-card {
            background-color: #d32f2f !important;
            color: white !important;
            padding: 2px;
            margin: 1px 0;
            font-size: 7px;
            page-break-inside: avoid;
          }
          
          .sick-leave-card {
            background-color: #ff5252 !important;
            color: white !important;
            padding: 2px;
            margin: 1px 0;
            font-size: 7px;
            page-break-inside: avoid;
          }
          
          .weekend-indicator {
            color: #ff9800;
            font-style: italic;
            font-size: 7px;
            margin-top: 2px;
          }
          
          .legend {
            margin-top: 20px;
            padding: 10px;
            border: 1px solid #ddd;
            font-size: 9px;
            page-break-inside: avoid;
          }
          
          .legend-item {
            display: inline-block;
            margin-right: 15px;
          }
          
          .legend-color {
            display: inline-block;
            width: 12px;
            height: 12px;
            margin-right: 3px;
            border-radius: 2px;
          }
          
          .footer {
            margin-top: 20px;
            text-align: center;
            font-size: 8px;
            color: #666;
            page-break-inside: avoid;
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">My Monthly Schedule</div>
          <div class="print-subtitle">${formatMonthYear()}</div>
          <div class="print-info">${userInfo?.name} - ${userInfo?.jobtitle || 'Staff'}</div>
          <div class="print-info">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
        </div>
        
        <table class="month-calendar">
          <thead>
            <tr>
              <th>Mon</th>
              <th>Tue</th>
              <th>Wed</th>
              <th>Thu</th>
              <th>Fri</th>
              <th>Sat</th>
              <th>Sun</th>
            </tr>
          </thead>
          <tbody>
            ${Array.from({ length: 6 }, (_, weekIndex) => `
              <tr>
                ${Array.from({ length: 7 }, (_, dayIndex) => {
                  const day = monthInfo.days[weekIndex * 7 + dayIndex];
                  if (!day) return '<td></td>';
                  
                  const shifts = myShifts[day.date] || [];
                  const availabilities = myAvailabilities[day.date] || [];
                  
                  let cellClass = day.isCurrentMonth ? 'current-month' : 'other-month';
                  if (day.isToday) cellClass += ' today-cell';
                  
                  return `
                    <td class="${cellClass}">
                      <div class="day-number ${day.isToday ? 'today-number' : ''}">
                        ${day.dayOfMonth}
                        ${day.isWeekend ? '<span class="weekend-indicator">(Weekend)</span>' : ''}
                      </div>
                      
                      ${shifts.map(shift => {
                        let shiftClass = 'shift-card ';
                        if (shift.isCancelled) shiftClass += 'shift-cancelled';
                        else if (shift.isAgencyFilled) shiftClass += 'shift-agency';
                        else if (shift.isWeekend) shiftClass += 'shift-weekend';
                        else shiftClass += 'shift-assigned';
                        
                        return `
                          <div class="${shiftClass}">
                            <strong>${shift.startTime}-${shift.endTime}</strong><br>
                            ${shift.ward}
                          </div>
                        `;
                      }).join('')}
                      
                      ${availabilities.map(avail => {
                        if (avail.isUnavailable || avail.isSickLeave) {
                          const cardClass = avail.isSickLeave ? 'sick-leave-card' : 'unavailable-card';
                          const reason = avail.isSickLeave ? avail.sicknessReason : avail.unavailabilityReason;
                          return `
                            <div class="${cardClass}">
                              <strong>${avail.startTime}-${avail.endTime}</strong><br>
                              ${avail.isSickLeave ? '🏥 SICK' : '⛔ UNAVAILABLE'}<br>
                              ${reason || ''}
                            </div>
                          `;
                        } else {
                          let availClass = 'availability-card ';
                          if (avail.status === 'Approved') availClass += 'availability-approved';
                          else if (avail.status === 'Rejected') availClass += 'availability-rejected';
                          else availClass += 'availability-pending';
                          
                          return `
                            <div class="${availClass}" style="border-left-color: ${getAvailabilityBorderColor(avail)};">
                              <strong>${avail.startTime}-${avail.endTime}</strong><br>
                              Available (${avail.status})<br>
                              ${avail.ward}
                            </div>
                          `;
                        }
                      }).join('')}
                    </td>
                  `;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="legend">
          <span class="legend-item"><span class="legend-color" style="background-color: #4caf50;"></span> Assigned Shift</span>
          <span class="legend-item"><span class="legend-color" style="background-color: #ff9800;"></span> Weekend Shift</span>
          <span class="legend-item"><span class="legend-color" style="background-color: #9c27b0;"></span> Agency Filled</span>
          <span class="legend-item"><span class="legend-color" style="background-color: #f44336;"></span> Cancelled</span>
          <span class="legend-item"><span class="legend-color" style="background-color: #d32f2f;"></span> Unavailable</span>
          <span class="legend-item"><span class="legend-color" style="background-color: #ff5252;"></span> Sick Leave</span>
          <span class="legend-item"><span class="legend-color" style="background-color: #e3f2fd;"></span> Pending Availability</span>
          <span class="legend-item"><span class="legend-color" style="background-color: #e8f5e8;"></span> Approved Availability</span>
        </div>
        
        <div class="footer">
          Beaumont Hospital Staff Scheduling System<br>
          Generated on ${new Date().toLocaleDateString()}<br>
          For questions, contact: scheduling@beaumont.ie
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(() => {
              window.print();
              setTimeout(() => window.close(), 100);
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loadingShifts || loadingAvailabilities) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PrintStyles />
      
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">My Monthly View</Typography>
      </Breadcrumbs>
      
      {/* Header with title and buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          My Monthly Schedule
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#673ab7',
              '&:hover': {
                bgcolor: '#5e35b1'
              }
            }}
            className="no-print"
          >
            Print Schedule
          </Button>
        </Box>
      </Box>
      
      {/* Year and Month Navigation */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Year Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigateYear(-1)} size="small">
                <KeyboardArrowLeft />
              </IconButton>
              <FormControl size="small" sx={{ minWidth: 100, mx: 1 }}>
                <Select
                  value={selectedYear}
                  onChange={handleYearChange}
                  displayEmpty
                  sx={{ bgcolor: 'white' }}
                >
                  {yearOptions.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton onClick={() => navigateYear(1)} size="small">
                <KeyboardArrowRight />
              </IconButton>
            </Box>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            
            {/* Month Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigateMonth(-1)}>
                <ArrowBackIos />
              </IconButton>
              <FormControl size="small" sx={{ minWidth: 140, mx: 1 }}>
                <Select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  displayEmpty
                  sx={{ bgcolor: 'white' }}
                >
                  {MONTHS.map((month, index) => (
                    <MenuItem key={month} value={index}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton onClick={() => navigateMonth(1)}>
                <ArrowForwardIos />
              </IconButton>
            </Box>
            
            <Button 
              startIcon={<Today />} 
              onClick={goToCurrentMonth}
              variant="outlined"
              size="small"
            >
              Current Month
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarMonth color="action" />
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
              {formatMonthYear()}
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Staff Info Card */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd' }} className="no-print">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body1">
              <strong>Name:</strong> {userInfo?.name}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body1">
              <strong>Job Title:</strong> {userInfo?.jobtitle || 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body1">
              <strong>Email:</strong> {userInfo?.email}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Month Calendar */}
      <Paper elevation={3} sx={{ overflow: 'hidden' }} className="print-page">
        <Box sx={{ minWidth: '1000px' }}>
          {/* Day headers */}
          <Grid container sx={{ bgcolor: 'primary.main', color: 'white' }}>
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
              <Grid item xs={12/7} key={day} sx={{ textAlign: 'center', p: 2 }}>
                <Typography fontWeight="bold">{day}</Typography>
              </Grid>
            ))}
          </Grid>
          
          {/* Calendar grid */}
          <Grid container>
            {monthInfo.days.map((day, index) => {
              const shifts = myShifts[day.date] || [];
              const availabilities = myAvailabilities[day.date] || [];
              
              return (
                <Grid 
                  item 
                  xs={12/7} 
                  key={day.date}
                  sx={{ 
                    minHeight: '120px',
                    borderRight: index % 7 !== 6 ? '1px solid #ddd' : 'none',
                    borderBottom: '1px solid #ddd',
                    bgcolor: !day.isCurrentMonth ? '#f5f5f5' : (day.isToday ? '#e3f2fd' : 'white'),
                    p: 1,
                    position: 'relative',
                  }}
                >
                  {/* Day number */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: day.isToday ? 'bold' : 'normal',
                      color: day.isToday ? 'primary.main' : (day.isCurrentMonth ? 'text.primary' : 'text.disabled'),
                      mb: 1,
                    }}
                  >
                    {day.dayOfMonth}
                    {day.isWeekend && day.isCurrentMonth && (
                      <Chip 
                        label="Weekend" 
                        size="small" 
                        sx={{ 
                          ml: 1,
                          height: 16,
                          fontSize: '0.6rem',
                          bgcolor: 'warning.light',
                          color: 'white',
                        }} 
                      />
                    )}
                  </Typography>
                  
                  {/* Shifts */}
                  {shifts.map((shift) => (
                    <Tooltip
                      key={shift.id}
                      title={
                        <div>
                          <div><strong>Time:</strong> {shift.startTime} - {shift.endTime}</div>
                          <div><strong>Ward:</strong> {shift.ward}</div>
                          <div><strong>Category:</strong> {shift.categoryOfStaff}</div>
                          {shift.location && <div><strong>Location:</strong> {shift.location}</div>}
                          {shift.isWeekend && <div><strong>Weekend Shift</strong></div>}
                          {shift.isAgencyFilled && <div><strong>Agency Filled</strong></div>}
                          {shift.isCancelled && <div><strong>Cancelled</strong></div>}
                        </div>
                      }
                      arrow
                    >
                      <Card 
                        sx={{ 
                          mb: 0.5, 
                          bgcolor: getShiftColor(shift),
                          color: 'white',
                          cursor: 'default',
                          '&:hover': {
                            boxShadow: 2,
                          }
                        }}
                      >
                        <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold', display: 'block' }}>
                            {shift.startTime}-{shift.endTime}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block' }}>
                            {shift.ward}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Tooltip>
                  ))}
                  
                  {/* Availabilities */}
                  {availabilities.map((avail) => {
                    const isUnavailable = avail.isUnavailable || avail.isSickLeave;
                    
                    return (
                      <Tooltip
                        key={avail.id}
                        title={
                          isUnavailable ? (
                            <div>
                              <div><strong>{avail.isSickLeave ? 'SICK LEAVE' : 'UNAVAILABLE'}</strong></div>
                              <div><strong>Time:</strong> {avail.startTime} - {avail.endTime}</div>
                              {avail.isSickLeave && <div><strong>Type:</strong> {avail.sickLeaveType}</div>}
                              {!avail.isSickLeave && <div><strong>Type:</strong> {avail.unavailabilityType}</div>}
                              <div><strong>Reason:</strong> {avail.sicknessReason || avail.unavailabilityReason || 'Not specified'}</div>
                              {avail.comments && <div><strong>Note:</strong> {avail.comments}</div>}
                            </div>
                          ) : (
                            <div>
                              <div><strong>Available</strong></div>
                              <div><strong>Time:</strong> {avail.startTime} - {avail.endTime}</div>
                              <div><strong>Status:</strong> {avail.status}</div>
                              <div><strong>Duration:</strong> {formatHoursMinutes(avail.durationMinutes)}</div>
                              {avail.ward && <div><strong>Ward:</strong> {avail.ward}</div>}
                              {avail.comments && <div><strong>Note:</strong> {avail.comments}</div>}
                            </div>
                          )
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            p: 0.5,
                            mb: 0.5,
                            borderRadius: 1,
                            bgcolor: getAvailabilityColor(avail),
                            color: isUnavailable ? 'white' : 'inherit',
                            borderLeft: isUnavailable ? '3px solid #b71c1c' : `3px solid ${getAvailabilityBorderColor(avail)}`,
                            fontSize: '0.65rem',
                            cursor: 'default',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold', display: 'block' }}>
                            {avail.startTime}-{avail.endTime}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.55rem', display: 'block' }}>
                            {avail.isSickLeave ? '🏥 SICK' : (avail.isUnavailable ? '⛔ UNAVAILABLE' : `Available (${avail.status})`)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  
                  {/* Empty state */}
                  {shifts.length === 0 && availabilities.length === 0 && day.isCurrentMonth && (
                    <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic', fontSize: '0.6rem' }}>
                      No shifts or availability
                    </Typography>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Paper>
      
      {/* Legend */}
      <Paper elevation={1} sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }} className="no-print">
        <Typography variant="subtitle2" gutterBottom>
          Legend:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.assigned, borderRadius: 1 }} />
            <Typography variant="caption">Assigned Shift</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.weekendAssigned, borderRadius: 1 }} />
            <Typography variant="caption">Weekend Shift</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.agency, borderRadius: 1 }} />
            <Typography variant="caption">Agency Filled</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.cancelled, borderRadius: 1 }} />
            <Typography variant="caption">Cancelled</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.unavailability, borderRadius: 1 }} />
            <Typography variant="caption">Unavailable</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.sickLeave, borderRadius: 1 }} />
            <Typography variant="caption">Sick Leave</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.availabilityPending, borderRadius: 1, border: '1px dashed #2196f3' }} />
            <Typography variant="caption">Pending Availability</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: STATUS_COLORS.availabilityApproved, borderRadius: 1, border: '1px dashed #4caf50' }} />
            <Typography variant="caption">Approved Availability</Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        className="no-print"
      />
    </Box>
  );
};

export default MyMonthlyView;